/**
 * Site platform detection via Firecrawl-backed backend + client HTML fallback.
 *
 * Cascade:
 *   1. Instant domain heuristics (caller seeds UI)
 *   2. POST /api/v1/detect-platform (Firecrawl)
 *   3. Client HTML scrape + inferPlatformFromHtml when API is missing,
 *      fails, or returns generic / low confidence — custom domains need this.
 */

import type { DetectSitePlatformResult } from "@/lib/types/platform"
import {
  inferPlatformFromDomain,
  inferPlatformFromHtml,
  parsePlatformFromApiPayload,
  preferStrongerInference,
  type PlatformInference,
} from "@/lib/utils/detectPlatform"

const PUBLIC_IS_PROD = import.meta.env.VITE_PUBLIC_IS_PROD
const PUBLIC_IS_LOCAL = import.meta.env.VITE_PUBLIC_IS_LOCAL
const PUBLIC_API_URL = import.meta.env.VITE_PUBLIC_API_URL

const API_URL = PUBLIC_API_URL
  ? `${PUBLIC_API_URL}/api/v1/`
  : PUBLIC_IS_PROD === "TRUE"
    ? `https://app.humblytics.com/api/v1/`
    : PUBLIC_IS_LOCAL === "TRUE"
      ? `http://localhost:3000/api/v1/`
      : `https://humblytics.onrender.com/api/v1/`

/** Domain confidence at/above this skips the HTML scrape (hosted subdomain already sure). */
const STRONG_DOMAIN_CONFIDENCE = 0.7
/** API / scrape results below this are treated as weak and lose to a better HTML match. */
const WEAK_RESULT_CONFIDENCE = 0.5

type DetectSitePlatformOptions = {
  domain: string
  token: string
  /** Optional absolute URL — defaults to https://{domain} */
  url?: string
  propertyId?: string
  signal?: AbortSignal
}

function domainFallback(domain: string): DetectSitePlatformResult {
  const inferred = inferPlatformFromDomain(domain)
  return {
    platform: inferred.platform,
    confidence: inferred.confidence,
    source: inferred.platform === "generic" ? "fallback" : "domain",
    signals: inferred.signals,
  }
}

function resolveTargetUrl(domain: string, url?: string): string {
  if (url && /^https?:\/\//i.test(url)) return url
  const host = (url || domain).replace(/^https?:\/\//i, "").replace(/\/+$/, "")
  return `https://${host}`
}

function toResult(
  inference: PlatformInference,
  source: DetectSitePlatformResult["source"],
  label?: string,
): DetectSitePlatformResult {
  return {
    platform: inference.platform,
    confidence: inference.confidence,
    source:
      inference.platform === "generic" && source !== "firecrawl" && source !== "html"
        ? "fallback"
        : source,
    signals: inference.signals,
    label,
  }
}

function needsHtmlEnrichment(result: DetectSitePlatformResult): boolean {
  if (result.platform === "generic") return true
  if (result.confidence < WEAK_RESULT_CONFIDENCE) return true
  return false
}

async function fetchText(
  url: string,
  signal: AbortSignal | undefined,
  headers?: Record<string, string>,
): Promise<string | null> {
  try {
    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "text/html,text/plain,*/*", ...headers },
      signal,
    })
    if (!response.ok) return null
    const text = await response.text()
    return text?.slice(0, 180_000) || null
  } catch {
    return null
  }
}

/**
 * Client-side page fetch for fingerprinting when the Firecrawl route is
 * unavailable or unsure. Prefers raw HTML (preserves meta/data-* attrs);
 * falls back to Jina reader markdown (CDN URLs usually survive).
 */
async function scrapeHtmlForFingerprinting(
  targetUrl: string,
  signal?: AbortSignal,
): Promise<string | null> {
  const encoded = encodeURIComponent(targetUrl)

  // 1) allorigins — raw HTML through a CORS proxy
  const raw = await fetchText(
    `https://api.allorigins.win/raw?url=${encoded}`,
    signal,
  )
  if (raw && raw.length > 200) return raw

  // 2) Jina reader — markdown/text with CDN hosts intact
  const jina = await fetchText(`https://r.jina.ai/${targetUrl}`, signal, {
    Accept: "text/plain",
  })
  if (jina && jina.length > 80) return jina

  return raw || jina
}

async function enrichWithHtmlScrape(
  current: DetectSitePlatformResult,
  targetUrl: string,
  signal?: AbortSignal,
): Promise<DetectSitePlatformResult> {
  if (current.confidence >= STRONG_DOMAIN_CONFIDENCE && current.platform !== "generic") {
    return current
  }

  const html = await scrapeHtmlForFingerprinting(targetUrl, signal)
  if (!html) return current

  const fromHtml = inferPlatformFromHtml(html)
  const winner = preferStrongerInference(
    {
      platform: current.platform,
      confidence: current.confidence,
      signals: current.signals || [],
    },
    fromHtml,
  )

  if (winner.platform === current.platform && winner.confidence <= current.confidence) {
    return current
  }

  return toResult(
    winner,
    winner.confidence > current.confidence && fromHtml.platform === winner.platform
      ? "html"
      : current.source,
    current.label,
  )
}

/**
 * Detect CMS / site builder for install instructions.
 * Always resolves — never throws — so onboarding UI can stay unblocked.
 */
export async function detectSitePlatform(
  options: DetectSitePlatformOptions,
): Promise<DetectSitePlatformResult> {
  const { domain, token, url, propertyId, signal } = options
  const fallback = domainFallback(domain)
  const targetUrl = resolveTargetUrl(domain, url)

  if (!domain) return fallback

  // No auth — still try HTML scrape for custom domains (install picker works pre-token in some flows).
  if (!token) {
    if (needsHtmlEnrichment(fallback)) {
      return enrichWithHtmlScrape(fallback, targetUrl, signal)
    }
    return fallback
  }

  try {
    const response = await fetch(`${API_URL}detect-platform`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
      credentials: "include",
      signal,
      body: JSON.stringify({
        domain,
        url: targetUrl,
        propertyId,
      }),
    })

    // Endpoint missing / not implemented → domain + HTML scrape.
    if (response.status === 404 || response.status === 501) {
      return enrichWithHtmlScrape(fallback, targetUrl, signal)
    }

    // Auth / server errors — still scrape so custom domains aren't stuck on generic.
    if (!response.ok) {
      return enrichWithHtmlScrape(fallback, targetUrl, signal)
    }

    const contentType = response.headers.get("content-type") || ""
    if (!contentType.includes("application/json")) {
      return enrichWithHtmlScrape(fallback, targetUrl, signal)
    }

    const payload = await response.json()
    const parsed = parsePlatformFromApiPayload(payload)

    if (!parsed) {
      return enrichWithHtmlScrape(fallback, targetUrl, signal)
    }

    let result: DetectSitePlatformResult = {
      platform: parsed.platform,
      confidence: parsed.confidence,
      source: "firecrawl",
      signals: parsed.signals,
      label: parsed.label,
    }

    // Prefer a strong domain heuristic over a weak/generic API guess.
    if (
      result.confidence < 0.4 &&
      fallback.confidence >= result.confidence &&
      fallback.platform !== "generic"
    ) {
      result = fallback
    } else {
      const vsDomain = preferStrongerInference(
        {
          platform: fallback.platform,
          confidence: fallback.confidence,
          signals: fallback.signals || [],
        },
        {
          platform: result.platform,
          confidence: result.confidence,
          signals: result.signals || [],
        },
      )
      if (vsDomain.platform === fallback.platform && vsDomain.confidence > result.confidence) {
        result = fallback
      }
    }

    // Custom domains: API often returns generic — scrape HTML to recover.
    if (needsHtmlEnrichment(result)) {
      result = await enrichWithHtmlScrape(result, targetUrl, signal)
    }

    return result
  } catch {
    if (signal?.aborted) return fallback
    return enrichWithHtmlScrape(fallback, targetUrl, signal)
  }
}
